package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.Rect

/** Plain selection state supports canvas hit rules and the accessible tree's broader selection. */
internal class StudioSelection {
    var ids: Set<String> = emptySet()
        private set
    var groupId: String? = null
        private set
    var editingText = false
    private val parents = mutableMapOf<String, String?>()
    fun restore(ids: Set<String>) { this.ids = ids.toSet(); groupId = null; editingText = false; parents.clear() }
    fun click(page: Page, point: Point, geometry: EditorGeometry, shift: Boolean = false, deep: Boolean = false, zoom: Double = 1.0) {
        val hit = geometry.hitTest(page.layers, point, 4 / zoom)
        val path = hit?.let { pathTo(page.layers, it) }.orEmpty()
        val scope = groupId?.let { id -> path.indexOfFirst { it.base.id == id } + 1 } ?: 0
        val id = (if (deep) path.lastOrNull() else path.getOrNull(scope))?.takeUnless { it is BackgroundLayer }?.base?.id
        if (id == null) { if (!shift) ids = emptySet(); return }
        ids = if (shift) { if (id in ids) ids - id else ids + id } else setOf(id)
    }
    fun selectFromTree(page: Page, id: String, toggle: Boolean = false, extend: Boolean = false) {
        if (pathTo(page.layers, id).isEmpty()) return
        ids = if (toggle && id in ids) ids - id else if (toggle || extend) ids + id else setOf(id)
    }
    fun enter(page: Page) {
        val selected = allLayers(page.layers).find { it.base.id == ids.singleOrNull() }
        if (selected is GroupLayer) { parents[selected.base.id] = pathTo(page.layers, selected.base.id).dropLast(1).lastOrNull()?.base?.id; groupId = selected.base.id; ids = selected.children.firstOrNull()?.let { setOf(it.base.id) }.orEmpty() }
    }
    fun parent(page: Page) {
        val path = ids.singleOrNull()?.let { pathTo(page.layers, it) }.orEmpty()
        path.dropLast(1).lastOrNull()?.let { ids = setOf(it.base.id); groupId = path.dropLast(2).lastOrNull()?.base?.id }
    }
    fun escape() {
        when { editingText -> editingText = false; groupId != null -> { ids = setOf(groupId!!); groupId = parents[groupId] }; else -> ids = emptySet() }
    }
    fun marquee(page: Page, rect: Rect, geometry: EditorGeometry, add: Boolean = false) {
        val scope = groupId?.let { allLayers(page.layers).find { layer -> layer.base.id == it } as? GroupLayer }?.children ?: page.layers
        val found = scope.filter { !it.base.hidden && !it.base.locked && it !is BackgroundLayer && geometry.bounds(page, it).intersects(rect) }
            .map { it.base.id }.toSet()
        ids = if (add) ids + found else found
    }
}

/** Full ancestor paths let deep selection and tree navigation agree for nested groups. */
internal fun pathTo(layers: List<Layer>, id: String): List<Layer> {
    for (layer in layers) {
        if (layer.base.id == id) return listOf(layer)
        if (layer is GroupLayer) pathTo(layer.children, id).takeIf { it.isNotEmpty() }?.let { return listOf(layer) + it }
    }
    return emptyList()
}
